/**
 * useTablePubSub — Real-time PubSub hook for Batch Tables.
 *
 * Connects to the Azure Web PubSub "ai_processing" hub, joins the
 * table's group (table_uuid), and dispatches Redux actions on incoming
 * cell/row/table events — fully replacing the 5-second polling.
 *
 * Events handled:
 *   table.cell_completed      → BATCH_TABLES_UPDATE_CELL
 *   table.row_completed       → BATCH_TABLES_UPDATE_ROW_STATUS
 *   table.row_failed          → BATCH_TABLES_UPDATE_ROW_STATUS
 *   table.processing_progress → BATCH_TABLES_UPDATE_TABLE_STATUS
 *   table.processing_completed→ BATCH_TABLES_UPDATE_TABLE_STATUS + silent refetch
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch } from '../../store';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import {
  BATCH_TABLES_UPDATE_CELL,
  BATCH_TABLES_UPDATE_ROW_STATUS,
  BATCH_TABLES_UPDATE_TABLE_STATUS,
  fetchBatchTableDetail,
} from '../../store/actions/BatchTables/BatchTablesActions';

const HUB = 'ai_processing';

/**
 * PubSub event payloads emitted by the AI engine.
 */
interface CellCompletedPayload {
  table_uuid: string;
  row_id: number;
  column_id: number;
  cell: {
    value: string | null;
    source_excerpt: string | null;
    confidence: 'high' | 'medium' | 'low' | 'not_found' | null;
    status: 'completed' | 'error';
  };
}

interface RowCompletedPayload {
  table_uuid: string;
  row_id: number;
  status: 'completed' | 'failed';
  file_name: string;
  processing_error: string | null;
}

interface ProcessingProgressPayload {
  table_uuid: string;
  cells_completed: number;
  cells_total: number;
  status: string;
}

interface ProcessingCompletedPayload {
  table_uuid: string;
  status: 'completed' | 'partial';
  cells_completed: number;
  cells_total: number;
  rows_completed: number;
  rows_failed: number;
}

/**
 * Hook: subscribe to real-time table processing events via PubSub.
 *
 * @param tableUuid  The table UUID to subscribe to (also the PubSub group).
 * @param enabled    Whether to connect (e.g. only when table is processing).
 */
export function useTablePubSub(tableUuid: string | undefined, enabled: boolean = true) {
  const dispatch = useAppDispatch();
  const handlerRef = useRef<((...args: any[]) => void) | null>(null);
  const groupRef = useRef<string | null>(null);
  const connectedRef = useRef(false);

  // ── Event handler (stable via ref) ──────────────────────────────────
  // IMPORTANT: MultiHubSocketService wildcard ('*') callback signature is:
  //   callback(...eventArgs, eventName)
  // i.e. the event name is the LAST argument, data is first.
  const handleEvent = useCallback(
    (data: any, eventName: string) => {
      if (!data || typeof data !== 'object') return;

      // The actual payload is nested inside `data.data` from the PubSub envelope
      const payload = data.data || data;

      switch (eventName) {
        case 'table.cell_completed': {
          const cellData = payload as CellCompletedPayload;
          dispatch({
            type: BATCH_TABLES_UPDATE_CELL,
            payload: {
              row_id: cellData.row_id,
              column_id: cellData.column_id,
              cell: cellData.cell,
            },
          });
          break;
        }

        case 'table.row_completed':
        case 'table.row_failed': {
          const rowData = payload as RowCompletedPayload;
          dispatch({
            type: BATCH_TABLES_UPDATE_ROW_STATUS,
            payload: {
              row_id: rowData.row_id,
              status: rowData.status,
              processing_error: rowData.processing_error ?? null,
            },
          });
          break;
        }

        case 'table.processing_progress': {
          const progressData = payload as ProcessingProgressPayload;
          dispatch({
            type: BATCH_TABLES_UPDATE_TABLE_STATUS,
            payload: {
              uuid: progressData.table_uuid,
              status: progressData.status || 'processing',
              cells_completed: progressData.cells_completed,
              cells_total: progressData.cells_total,
            },
          });
          break;
        }

        case 'table.processing_completed': {
          const completedData = payload as ProcessingCompletedPayload;
          dispatch({
            type: BATCH_TABLES_UPDATE_TABLE_STATUS,
            payload: {
              uuid: completedData.table_uuid,
              status: completedData.status,
              cells_completed: completedData.cells_completed,
              cells_total: completedData.cells_total,
            },
          });

          // Silent refetch to sync full state from server
          if (completedData.table_uuid) {
            dispatch(fetchBatchTableDetail(completedData.table_uuid) as any);
          }
          break;
        }

        default:
          // Ignore other events on this hub
          break;
      }
    },
    [dispatch],
  );

  // ── Connect / disconnect lifecycle ──────────────────────────────────
  useEffect(() => {
    if (!tableUuid || !enabled) return;

    let cancelled = false;
    const service = getMultiHubSocketService();

    const setup = async () => {
      try {
        const connected = await service.connectToHub(HUB);
        if (cancelled || !connected) return;

        connectedRef.current = true;

        // Join the table's group
        service.emit(HUB, 'join', { group: tableUuid });
        groupRef.current = tableUuid;

        // Register wildcard listener — callback receives (data, eventName) from MultiHubSocketService
        const handler = (data: any, eventName: string) => handleEvent(data, eventName);
        handlerRef.current = handler;
        service.on(HUB, '*', handler);
      } catch (err) {
        console.error('[useTablePubSub] Failed to connect:', err);
      }
    };

    setup();

    // ── Cleanup ──────────────────────────────────────────────────────
    return () => {
      cancelled = true;

      try {
        const svc = getMultiHubSocketService();

        // Leave group
        if (groupRef.current) {
          svc.emit(HUB, 'leave', { group: groupRef.current });
          groupRef.current = null;
        }

        // Remove listener
        if (handlerRef.current) {
          svc.off(HUB, '*', handlerRef.current);
          handlerRef.current = null;
        }

        connectedRef.current = false;
      } catch {
        // Best-effort cleanup
      }
    };
  }, [tableUuid, enabled, handleEvent]);
}

export default useTablePubSub;
